import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import adminTicketService from "../../services/adminTicketService";
import AdminPageHeader, { adminPageShellSx } from "../Shared/AdminPageHeader";
import { getStateFilterOptions, mergeTicketSnapshots } from "../../services/adminTicketUtils";
import { useAuth } from "../Context/authProvider";
import { useAdminTicketUnread } from "../Context/ticketUnreadContext";
import { useUI } from "../Context/UIContext";
import TicketsCard from "./TicketsCard";

const chipSx = {
  fontWeight: 700,
  fontFamily: "inherit",
  borderColor: "#0b3d87",
};

function renderTicketsBody({
  loading,
  tickets,
  expandedId,
  setExpandedId,
  loginId,
  isAdmin,
  loadTickets,
  handleMessagesRead,
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (tickets.length === 0) {
    return (
      <Typography sx={{ fontFamily: "Rubik", color: "#808080" }}>
        Nessun ticket trovato per il filtro selezionato.
      </Typography>
    );
  }
  return tickets.map((ticket) => (
    <TicketsCard
      key={ticket.id}
      ticket={ticket}
      expanded={expandedId === ticket.id}
      onToggle={() => setExpandedId((prev) => (prev === ticket.id ? null : ticket.id))}
      loginId={loginId}
      isAdmin={isAdmin}
      onTicketUpdated={loadTickets}
      onMessagesRead={handleMessagesRead}
    />
  ));
}

const GestioneTickets = () => {
  const { loginId, isAdmin } = useAuth();
  const { showError } = useUI();
  const { ticketSnapshots, refreshSummary } = useAdminTicketUnread();
  const [stateFilter, setStateFilter] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const filterOptions = useMemo(() => getStateFilterOptions(), []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminTicketService.listTickets({
        state: stateFilter || undefined,
        sortBy: "createdAt",
        sortDirection: "desc",
      });
      setTickets(res.items);
    } catch (e) {
      showError(e?.response?.data?.message ?? e?.message ?? "Errore caricamento ticket");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [stateFilter, showError]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (!ticketSnapshots.length) return;
    setTickets((prev) => mergeTicketSnapshots(prev, ticketSnapshots));
  }, [ticketSnapshots]);

  const handleMessagesRead = useCallback((ticketId) => {
    setTickets((prev) =>
      prev.map((t) => (t.id === ticketId ? { ...t, unreadCount: 0 } : t))
    );
    refreshSummary();
  }, [refreshSummary]);

  return (
    <Box sx={adminPageShellSx}>
      <AdminPageHeader
        title="Gestione Tickets"
        subtitle="Gestisci le richieste di assistenza e fornisci supporto ai clienti."
      />

      <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
        {filterOptions.map(({ value, label }) => (
          <Chip
            key={label}
            label={label}
            size="small"
            onClick={() => setStateFilter(value)}
            color={stateFilter === value ? "primary" : "default"}
            variant={stateFilter === value ? "filled" : "outlined"}
            sx={chipSx}
          />
        ))}
      </Box>

      {renderTicketsBody({
        loading,
        tickets,
        expandedId,
        setExpandedId,
        loginId,
        isAdmin,
        loadTickets,
        handleMessagesRead,
      })}
    </Box>
  );
};

export default GestioneTickets;
